export const GTFS_CORE_FILES = ['stops.txt', 'routes.txt', 'trips.txt', 'stop_times.txt'] as const;
// `agency.txt` is optional for import (we capture it for round-trip preservation),
// even though the GTFS spec marks it conditionally-required for feeds with >1 agency.
export const GTFS_OPTIONAL_FILES = ['agency.txt', 'shapes.txt', 'calendar.txt', 'calendar_dates.txt'] as const;

/**
 * Canonical order in which GTFS tables are written into an exported feed.
 * Ordering is cosmetic (zip entry order) but keeps generated archives stable
 * and easy to diff against agency-provided feeds.
 */
export const GTFS_EXPORT_FILES = [
  'agency.txt',
  'stops.txt',
  'routes.txt',
  'trips.txt',
  'stop_times.txt',
  'calendar.txt',
  'calendar_dates.txt',
  'shapes.txt',
  'feed_info.txt',
] as const;

type ZipFileEntry = {
  dir?: boolean;
};

type ZipLike = {
  files: Record<string, ZipFileEntry | undefined>;
};

export interface ResolvedGtfsZipEntries {
  requiredPaths: Map<string, string>;
  optionalPaths: Map<string, string>;
  missingRequired: string[];
  hasRequired: boolean;
}

export const normalizeZipEntryName = (entryName: string): string => {
  const normalizedPath = entryName.replace(/\\/g, '/');
  const fileName = normalizedPath.split('/').pop() || '';
  return fileName.trim().toLowerCase();
};

export const resolveGtfsZipEntries = (
  zip: ZipLike,
  requiredFiles: readonly string[] = GTFS_CORE_FILES,
  optionalFiles: readonly string[] = GTFS_OPTIONAL_FILES,
): ResolvedGtfsZipEntries => {
  const requiredSet = new Set(requiredFiles.map((fileName) => fileName.toLowerCase()));
  const optionalSet = new Set(optionalFiles.map((fileName) => fileName.toLowerCase()));
  const requiredPaths = new Map<string, string>();
  const optionalPaths = new Map<string, string>();

  Object.keys(zip.files).forEach((path) => {
    const entry = zip.files[path];
    if (!entry || entry.dir) return;

    const normalizedName = normalizeZipEntryName(path);
    if (!normalizedName) return;

    if (requiredSet.has(normalizedName) && !requiredPaths.has(normalizedName)) {
      requiredPaths.set(normalizedName, path);
      return;
    }

    if (optionalSet.has(normalizedName) && !optionalPaths.has(normalizedName)) {
      optionalPaths.set(normalizedName, path);
    }
  });

  const missingRequired = requiredFiles.filter((fileName) => !requiredPaths.has(fileName.toLowerCase()));

  return {
    requiredPaths,
    optionalPaths,
    missingRequired,
    hasRequired: missingRequired.length === 0,
  };
};

export const hasRequiredGtfsCoreFiles = (zip: ZipLike): boolean => {
  return resolveGtfsZipEntries(zip).hasRequired;
};
